import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

export default function Landing() {
  const { user } = useAuth();

  return (
    <div className="bg-pure-white dark:bg-space-black text-gray-900 dark:text-white dark:text-white overflow-hidden transition-colors duration-300">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        {/* Animated Background */}
        <div className="absolute inset-0">
          {/* Light mode background */}
          <img 
            src="/images/hero-bg-light.png" 
            alt="" 
            className="w-full h-full object-cover opacity-40 dark:hidden"
          />
          {/* Dark mode background */}
          <img 
            src="/images/hero-bg-v2.png" 
            alt="" 
            className="w-full h-full object-cover opacity-60 hidden dark:block"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-pure-white/50 dark:from-space-black/50 via-transparent to-pure-white dark:to-space-black"></div>
        </div>
        
        {/* Floating Icons */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[15%] left-[10%] w-16 h-16 animate-float opacity-10 dark:opacity-20">
            <img src="/images/moldclaw-icon.png" alt="" className="w-full h-full object-contain" />
          </div>
          <div className="absolute top-[25%] right-[15%] w-12 h-12 animate-float opacity-10 dark:opacity-15" style={{animationDelay: '2s'}}>
            <img src="/images/moldclaw-icon.png" alt="" className="w-full h-full object-contain" />
          </div>
          <div className="absolute bottom-[30%] left-[20%] w-10 h-10 animate-float opacity-5 dark:opacity-10" style={{animationDelay: '4s'}}>
            <img src="/images/moldclaw-icon.png" alt="" className="w-full h-full object-contain" />
          </div>
          <div className="absolute bottom-[20%] right-[25%] w-14 h-14 animate-float opacity-10 dark:opacity-20" style={{animationDelay: '1s'}}>
            <img src="/images/moldclaw-icon.png" alt="" className="w-full h-full object-contain" />
          </div>
        </div>
        
        {/* Glow Orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-space-blue/10 dark:bg-space-blue/20 rounded-full blur-[128px] animate-pulse-soft"></div>
        <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-purple-500/10 dark:bg-purple-500/15 rounded-full blur-[100px] animate-pulse-soft" style={{animationDelay: '2s'}}></div>

        {/* Hero Content */}
        <div className="relative z-10 section-container text-center px-4">
          {/* Badge */}
          <Link 
            to="/download"
            className="inline-flex items-center gap-3 bg-space-black/5 dark:bg-white/5 backdrop-blur-md border border-space-black/10 dark:border-white/10 rounded-full px-5 py-2.5 mb-8 animate-fade-in hover:bg-space-black/10 dark:hover:bg-white/10 transition-colors cursor-pointer"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            <span className="text-sm text-gray-600 dark:text-gray-300">v0.8.17 출시</span>
            <span className="text-xs text-gray-400 dark:text-gray-500">|</span>
            <span className="text-sm text-space-blue">릴리스 노트 보기 →</span>
          </Link>
          
          {/* Logo + Title */}
          <div className="flex items-center justify-center gap-4 mb-6 animate-fade-in" style={{animationDelay: '0.1s'}}>
            <img 
              src="/images/moldclaw-icon.png" 
              alt="moldClaw" 
              className="w-16 h-16 md:w-20 md:h-20 drop-shadow-[0_0_30px_rgba(37,99,235,0.5)]"
            />
          </div>
          
          {/* Main Title */}
          <h1 className="text-6xl md:text-8xl lg:text-9xl font-black tracking-tight mb-6 animate-fade-in" style={{animationDelay: '0.2s'}}>
            <span className="bg-gradient-to-r from-space-blue via-blue-400 to-cyan-400 bg-clip-text text-transparent drop-shadow-[0_0_30px_rgba(37,99,235,0.5)]">
              MOLDCLAW
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mb-4 leading-relaxed animate-fade-in" style={{animationDelay: '0.3s'}}>
            내 컴퓨터에서 돌아가는<br className="hidden sm:block" />
            <span className="text-gray-900 dark:text-white dark:text-white font-semibold">나만을 위한 OpenClaw 비서</span>
          </p>
          
          {/* Sub message */}
          <p className="text-lg md:text-xl text-gray-500 dark:text-gray-400 max-w-xl mx-auto mb-10 animate-fade-in" style={{animationDelay: '0.35s'}}>
            <span className="text-red-500 font-medium">터미널은 그만!</span> 클릭 한 번으로 AI 비서를 관리하세요
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in" style={{animationDelay: '0.4s'}}>
            <Link
              to={user ? '/download' : '/register'}
              className="group relative px-8 py-4 bg-space-blue text-white font-semibold rounded-xl overflow-hidden transition-all duration-300 hover:shadow-[0_0_40px_rgba(37,99,235,0.4)] hover:scale-105"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-blue-600 to-space-blue opacity-0 group-hover:opacity-100 transition-opacity"></span>
              <span className="relative flex items-center justify-center gap-2">
                무료로 시작하기
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </span>
            </Link>
            <a
              href="#comparison"
              className="px-8 py-4 bg-space-black/5 dark:bg-white/5 backdrop-blur-sm text-gray-900 dark:text-white dark:text-white font-semibold rounded-xl border border-space-black/10 dark:border-white/10 hover:bg-space-black/10 dark:hover:bg-white/10 hover:border-space-black/20 dark:hover:border-white/20 transition-all duration-300"
            >
              비교해보기
            </a>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <svg className="w-6 h-6 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </section>

      {/* Before/After Comparison Section */}
      <section id="comparison" className="relative py-24 md:py-32 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-50 dark:from-zinc-900/50 to-pure-white dark:to-space-black"></div>
        
        <div className="relative z-10 section-container">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500/10 to-green-500/10 backdrop-blur-sm px-5 py-2.5 rounded-full text-sm font-medium mb-6 border border-gray-200 dark:border-white/10">
              ⚡ Before → After
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-space-black dark:from-white to-gray-600 dark:to-gray-300 bg-clip-text text-transparent">
              이렇게 달라집니다
            </h2>
          </div>
          
          {/* Comparison Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Before - Terminal */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl blur opacity-20 group-hover:opacity-30 transition-opacity"></div>
              <div className="relative bg-zinc-900 rounded-2xl p-6 border border-zinc-700">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <span className="text-zinc-400 text-sm font-mono">터미널</span>
                  <span className="ml-auto px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">어려움</span>
                </div>
                
                {/* Terminal Content */}
                <div className="font-mono text-sm text-green-400 space-y-2 overflow-x-auto">
                  <p><span className="text-zinc-500">$</span> npm install -g openclaw</p>
                  <p><span className="text-zinc-500">$</span> openclaw config init</p>
                  <p><span className="text-zinc-500">$</span> openclaw gateway install</p>
                  <p><span className="text-zinc-500">$</span> nano ~/.openclaw/config.yaml</p>
                  <p className="text-zinc-500"># API 키 설정...</p>
                  <p className="text-zinc-500"># 메신저 연결 설정...</p>
                  <p><span className="text-zinc-500">$</span> openclaw gateway start</p>
                  <p className="text-yellow-400">⚠ Error: ANTHROPIC_API_KEY not set</p>
                </div>
                
                <div className="mt-6 pt-4 border-t border-zinc-700">
                  <ul className="space-y-2 text-sm text-zinc-400">
                    <li className="flex items-center gap-2">
                      <span className="text-red-400">✗</span> 명령어 암기 필요
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-red-400">✗</span> 설정 파일 직접 편집
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-red-400">✗</span> 에러 메시지 해독 필요
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* After - MoldClaw */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-green-500 to-cyan-500 rounded-2xl blur opacity-20 group-hover:opacity-30 transition-opacity"></div>
              <div className="relative bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-2xl p-6 border border-zinc-700">
                <div className="flex items-center gap-3 mb-4">
                  <img src="/images/moldclaw-icon.png" alt="" className="w-6 h-6" />
                  <span className="text-white text-sm font-medium">MoldClaw</span>
                  <span className="ml-auto px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">쉬움</span>
                </div>
                
                {/* MoldClaw UI Preview */}
                <div className="bg-zinc-800/50 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-zinc-300">API 키 설정</span>
                    <div className="w-32 h-8 bg-zinc-700 rounded-lg flex items-center px-3">
                      <span className="text-xs text-zinc-400">••••••••</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-zinc-300">텔레그램 연결</span>
                    <div className="w-12 h-6 bg-green-500 rounded-full flex items-center justify-end px-1">
                      <div className="w-4 h-4 bg-white rounded-full"></div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-zinc-300">Gateway 상태</span>
                    <span className="flex items-center gap-1.5 text-green-400 text-sm">
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                      실행 중
                    </span>
                  </div>
                </div>
                
                <div className="mt-6 pt-4 border-t border-zinc-700">
                  <ul className="space-y-2 text-sm text-zinc-300">
                    <li className="flex items-center gap-2">
                      <span className="text-green-400">✓</span> 클릭만으로 설정 완료
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-400">✓</span> 직관적인 대시보드
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-400">✓</span> 실시간 상태 확인
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          {/* Arrow / CTA */}
          <div className="text-center mt-12">
            <Link
              to={user ? '/download' : '/register'}
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-green-500 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-green-500/25 transition-all hover:scale-105"
            >
              지금 바로 전환하기
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="relative py-24 md:py-32 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-pure-white dark:from-space-black via-gray-50 dark:via-space-black/80 to-pure-white dark:to-space-black"></div>
        </div>
        
        {/* Floating Accents */}
        <div className="absolute top-20 right-10 w-32 h-32 bg-space-blue/10 dark:bg-space-blue/20 rounded-full blur-[80px] animate-pulse-soft"></div>
        <div className="absolute bottom-20 left-10 w-40 h-40 bg-purple-500/10 dark:bg-purple-500/15 rounded-full blur-[100px] animate-pulse-soft" style={{animationDelay: '2s'}}></div>
        
        <div className="relative z-10 section-container">
          {/* Section Header */}
          <div className="text-center mb-16 md:mb-20">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-space-blue/10 to-purple-500/10 dark:from-space-blue/20 dark:to-purple-500/20 backdrop-blur-sm px-5 py-2.5 rounded-full text-sm font-medium mb-6 border border-gray-200 dark:border-white/10">
              <img src="/images/moldclaw-icon.png" alt="" className="w-5 h-5" />
              핵심 기능
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-space-black dark:from-white to-gray-600 dark:to-gray-300 bg-clip-text text-transparent">
              MoldClaw만의 장점
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              터미널 없이도 OpenClaw의 모든 기능을 사용하세요.<br />
              <span className="text-gray-900 dark:text-white dark:text-white font-medium">설치부터 설정까지, 클릭만으로 완료!</span>
            </p>
          </div>
          
          {/* Features Grid - Bento Style */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((feature, index) => (
              <div 
                key={index}
                className={`group relative overflow-hidden rounded-3xl transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-space-blue/10
                  ${index === 0 ? 'md:col-span-2 lg:col-span-1' : ''}`}
              >
                {/* Card Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-gray-50 dark:from-zinc-800/80 to-gray-100 dark:to-zinc-900/80 backdrop-blur-xl"></div>
                <div className="absolute inset-0 bg-gradient-to-br from-space-blue/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                {/* Border Glow */}
                <div className="absolute inset-0 rounded-3xl border border-gray-200 dark:border-white/5 group-hover:border-space-blue/30 transition-colors duration-500"></div>
                
                {/* Content */}
                <div className="relative p-7">
                  {/* Icon */}
                  <div className="w-14 h-14 bg-gradient-to-br from-space-blue to-blue-600 rounded-2xl flex items-center justify-center mb-5 shadow-lg shadow-space-blue/30 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                    <span className="text-2xl">{feature.icon}</span>
                  </div>
                  
                  <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white dark:text-white group-hover:text-space-blue transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="usecases" className="relative py-24 md:py-32 overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-pure-white dark:from-space-black via-gray-50 dark:via-zinc-900/50 to-pure-white dark:to-space-black"></div>
        
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-space-blue/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent"></div>
        
        <div className="relative z-10 section-container">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500/10 to-pink-500/10 dark:from-purple-500/20 dark:to-pink-500/20 backdrop-blur-sm px-5 py-2.5 rounded-full text-sm font-medium mb-6 border border-purple-200 dark:border-purple-500/20">
              💡 활용 사례
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-space-black dark:from-white via-purple-600 dark:via-purple-200 to-space-black dark:to-white bg-clip-text text-transparent">
              이렇게 활용하세요
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-xl mx-auto">
              일상의 다양한 순간에서 moldClaw가 도와드립니다.
            </p>
          </div>
          
          {/* Use Cases Grid - Card Style */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {useCases.map((useCase, index) => (
              <div 
                key={index}
                className="group relative overflow-hidden rounded-2xl transition-all duration-500 hover:-translate-y-2"
              >
                {/* Card */}
                <div className="relative bg-gradient-to-br from-gray-50 dark:from-zinc-800/90 to-gray-100 dark:to-zinc-900/90 backdrop-blur-xl p-6 border border-gray-200 dark:border-zinc-700/50 group-hover:border-purple-400 dark:group-hover:border-purple-500/30 rounded-2xl h-full transition-all duration-300">
                  {/* Top Accent Line */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-space-blue via-purple-500 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity rounded-t-2xl"></div>
                  
                  {/* Icon Badge */}
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-purple-500/10 to-pink-500/10 dark:from-purple-500/20 dark:to-pink-500/20 rounded-2xl mb-4 group-hover:scale-110 transition-transform">
                    <span className="text-3xl">{useCase.icon}</span>
                  </div>
                  
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white dark:text-white mb-3 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                    {useCase.title}
                  </h3>
                  
                  {/* Quote Box */}
                  <div className="bg-gray-100 dark:bg-zinc-800/50 rounded-xl p-3 mb-3 border-l-2 border-purple-500/50">
                    <p className="text-sm text-gray-700 dark:text-gray-300 italic">
                      "{useCase.example}"
                    </p>
                  </div>
                  
                  {/* Result */}
                  <div className="flex items-start gap-2">
                    <span className="text-purple-500 dark:text-purple-400 mt-0.5">→</span>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {useCase.result}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-space-blue/5 via-transparent to-transparent"></div>
        </div>
        
        <div className="relative z-10 section-container">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-green-500/10 text-green-600 dark:text-green-400 px-5 py-2.5 rounded-full text-sm font-medium mb-6 border border-green-200 dark:border-green-500/20">
              🚀 시작하기
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-space-black dark:from-white to-gray-600 dark:to-gray-300 bg-clip-text text-transparent">
              3단계로 시작하세요
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {steps.map((step, index) => (
              <div key={index} className="relative group">
                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-12 left-[55%] w-full h-0.5">
                    <div className="w-full h-full bg-gradient-to-r from-space-blue/50 via-space-blue/30 to-transparent"></div>
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-space-blue/50 rounded-full"></div>
                  </div>
                )}
                
                {/* Card */}
                <div className="relative bg-gradient-to-br from-gray-50 dark:from-zinc-800/50 to-gray-100 dark:to-zinc-900/50 backdrop-blur-sm rounded-3xl p-8 border border-gray-200 dark:border-zinc-700/50 group-hover:border-space-blue/30 transition-all duration-300 text-center">
                  {/* Step Number */}
                  <div className="relative inline-flex items-center justify-center w-20 h-20 mb-6">
                    <div className="absolute inset-0 bg-gradient-to-br from-space-blue to-blue-600 rounded-2xl rotate-3 group-hover:rotate-6 transition-transform"></div>
                    <div className="relative bg-gradient-to-br from-space-blue to-blue-600 rounded-2xl w-full h-full flex items-center justify-center shadow-xl shadow-space-blue/30">
                      <span className="text-3xl font-black text-white">{index + 1}</span>
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white dark:text-white">{step.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400">{step.description}</p>
                  
                  {/* Step Icons */}
                  <div className="mt-5 flex justify-center gap-2">
                    {index === 0 && <span className="text-2xl">📧</span>}
                    {index === 1 && <span className="text-2xl">💻</span>}
                    {index === 2 && <span className="text-2xl">🎉</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative py-32 md:py-40 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          {/* Light mode background */}
          <img 
            src="/images/hero-bg-light.png" 
            alt="" 
            className="w-full h-full object-cover opacity-30 dark:hidden"
          />
          {/* Dark mode background */}
          <img 
            src="/images/hero-bg-v2.png" 
            alt="" 
            className="w-full h-full object-cover opacity-40 hidden dark:block"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-pure-white dark:from-space-black via-pure-white/90 dark:via-space-black/90 to-pure-white/70 dark:to-space-black/70"></div>
        </div>
        
        {/* Glow Effects */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-space-blue/10 dark:bg-space-blue/20 rounded-full blur-[200px] animate-pulse-soft"></div>
        <div className="absolute top-1/4 right-1/4 w-80 h-80 bg-purple-500/10 dark:bg-purple-500/15 rounded-full blur-[150px] animate-pulse-soft" style={{animationDelay: '2s'}}></div>
        
        {/* Floating Icons */}
        <div className="absolute top-20 left-[15%] w-12 h-12 animate-float opacity-20 dark:opacity-30">
          <img src="/images/moldclaw-icon.png" alt="" className="w-full h-full object-contain" />
        </div>
        <div className="absolute bottom-32 right-[20%] w-10 h-10 animate-float opacity-15 dark:opacity-20" style={{animationDelay: '3s'}}>
          <img src="/images/moldclaw-icon.png" alt="" className="w-full h-full object-contain" />
        </div>
        
        <div className="relative z-10 section-container text-center">
          {/* Logo with Glow */}
          <div className="relative inline-block mb-10">
            <div className="absolute inset-0 bg-space-blue/50 blur-3xl rounded-full scale-150"></div>
            <img 
              src="/images/moldclaw-icon.png" 
              alt="moldClaw" 
              className="relative w-24 h-24 md:w-32 md:h-32 drop-shadow-[0_0_60px_rgba(37,99,235,0.8)]"
            />
          </div>
          
          <h2 className="text-4xl md:text-6xl font-black mb-6 bg-gradient-to-r from-space-black dark:from-white via-space-blue to-space-black dark:to-white bg-clip-text text-transparent">
            지금 바로 시작하세요
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-12 max-w-2xl mx-auto leading-relaxed">
            터미널 명령어는 잊으세요.<br />
            <span className="text-space-blue font-semibold">클릭 몇 번</span>이면 AI 비서가 준비됩니다.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-5 justify-center">
            <Link
              to={user ? '/download' : '/register'}
              className="group relative px-12 py-5 bg-gradient-to-r from-space-blue to-blue-600 text-white font-bold rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-[0_0_60px_rgba(37,99,235,0.5)] hover:scale-105"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-blue-400 to-space-blue opacity-0 group-hover:opacity-100 transition-opacity"></span>
              <span className="relative flex items-center justify-center gap-3">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                무료 다운로드
              </span>
            </Link>
            <a
              href="#comparison"
              className="px-12 py-5 bg-space-black/5 dark:bg-white/5 backdrop-blur-md text-gray-900 dark:text-white dark:text-white font-semibold rounded-2xl border border-space-black/10 dark:border-white/10 hover:bg-space-black/10 dark:hover:bg-white/10 hover:border-space-black/20 dark:hover:border-white/20 transition-all duration-300"
            >
              비교해보기
            </a>
          </div>
          
          {/* Trust Badges */}
          <div className="mt-16 flex flex-wrap gap-6 justify-center items-center text-gray-500 dark:text-gray-500 text-sm">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>무료 사용</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>터미널 불필요</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>100% 로컬</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const features = [
  {
    icon: '🖱️',
    title: '클릭만으로 설정 완료',
    description: '복잡한 명령어 대신 직관적인 UI로 모든 설정을 완료하세요. 터미널 지식이 없어도 됩니다.',
  },
  {
    icon: '📊',
    title: '실시간 대시보드',
    description: 'Gateway 상태, 메신저 연결, API 사용량을 한눈에 확인하세요.',
  },
  {
    icon: '💬',
    title: '메신저 간편 연결',
    description: '텔레그램, 디스코드를 몇 번의 클릭으로 연결. QR 코드 스캔 한 번이면 끝!',
  },
  {
    icon: '⚡',
    title: '원클릭 Gateway 관리',
    description: '시작, 중지, 재시작을 버튼 하나로. 로그도 실시간으로 확인 가능합니다.',
  },
  {
    icon: '🔧',
    title: 'GUI 설정 편집기',
    description: 'YAML 파일 직접 수정 대신 폼으로 설정. 오타 걱정 없이 안전하게.',
  },
  {
    icon: '🔒',
    title: '안전한 API 키 관리',
    description: 'API 키를 안전하게 저장하고 관리. 환경 변수 설정 필요 없음.',
  },
];

const useCases = [
  {
    icon: '👔',
    title: '스마트 코디 추천',
    example: '매일 아침 8시에 날씨 보고 오늘 입을 옷 추천해줘',
    result: '기온, 강수확률, 일정까지 고려한 맞춤 코디 제안',
  },
  {
    icon: '📧',
    title: '이메일 자동 분류',
    example: '광고 메일은 무시하고, 거래처 메일만 요약해서 보내줘',
    result: '중요 메일만 필터링해서 실시간 알림',
  },
  {
    icon: '📊',
    title: '주간 리포트 생성',
    example: '이번 주 캘린더, 메일, 메모 정리해서 일요일에 보내줘',
    result: '한 주를 정리한 개인 주간보고서 자동 생성',
  },
  {
    icon: '🛫',
    title: '여행 플래너',
    example: '다음 달 제주도 2박 3일 여행 일정 짜줘',
    result: '동선 최적화된 상세 여행 계획표 제공',
  },
  {
    icon: '💰',
    title: '가계부 분석',
    example: '지난달 카드 내역 분석해서 어디서 돈 많이 썼는지 알려줘',
    result: '카테고리별 지출 분석 및 절약 포인트 제안',
  },
  {
    icon: '🎯',
    title: '프로젝트 트래킹',
    example: '노션 보드 확인하고 마감 임박한 태스크 알려줘',
    result: '데드라인 48시간 전 자동 리마인더',
  },
];

const steps = [
  {
    title: '회원가입',
    description: '간단한 이메일 인증으로 계정을 만드세요.',
  },
  {
    title: '다운로드',
    description: 'Windows, macOS, Linux 중 선택하여 설치하세요.',
  },
  {
    title: '사용 시작',
    description: '메신저를 연결하고 AI 비서와 대화를 시작하세요.',
  },
];
